import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const steps = [
  {
    step: "STEP 1",
    icon: "ri-file-list-3-line",
    title: "Submit Your Preferences",
    desc: "Share your preferred diamond specifications including shape, carat size, color, clarity, and budget range.",
  },
  {
    step: "STEP 2",
    icon: "ri-vip-diamond-line",
    title: "Curated Diamond Options",
    desc: "Receive carefully sourced IGI-certified selections complete with videos, certification details, and pricing.",
  },
  {
    step: "STEP 3",
    icon: "ri-search-eye-line",
    title: "Review & Approval",
    desc: "Review your curated options with personalized guidance before confirming your selected stone.",
  },
  {
    step: "STEP 4",
    icon: "ri-truck-line",
    title: "Secure Delivery",
    desc: "Your selected diamond is sourced, verified, and securely delivered within the estimated timeframe.",
  },
];

const guarantees = [
  { icon: "ri-shield-check-line", title: "IGI-CERTIFIED", desc: "Every diamond is independently certified for your confidence." },
  { icon: "ri-user-heart-line", title: "PERSONAL GUIDANCE", desc: "Expert support throughout your entire sourcing journey." },
  { icon: "ri-lock-line", title: "SECURE & PRIVATE", desc: "Your information and preferences are always protected." },
  { icon: "ri-global-line", title: "GLOBAL SOURCING", desc: "Access to the world's finest lab-grown diamonds." },
];

export default function Process() {
  const heroRef = useScrollReveal<HTMLDivElement>();
  const stepsRef = useScrollReveal<HTMLDivElement>();
  const timelineRef = useScrollReveal<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Our Process - Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-[130px] md:pt-[170px] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[55vh]">
          {/* Left Content */}
          <div className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-24 reveal" ref={heroRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">OUR PROCESS</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
              A Refined Sourcing Process
            </h2>
            <div className="h-px w-16 bg-zayan-gold mb-8" />
            <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
              Zayan Atelier offers a personalized diamond sourcing experience designed around transparency, guidance, and carefully curated selections.
            </p>
          </div>

          {/* Right Image */}
          <div className="relative overflow-hidden h-64 sm:h-80 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=round%20brilliant%20cut%20diamond%20held%20by%20golden%20metal%20tweezers%20against%20dark%20black%20background%2C%20luxury%20gemstone%20photography%2C%20dramatic%20lighting%2C%20extreme%20close%20up%2C%20sparkling%20facets%2C%20professional%20studio%20shot%2C%20ultra%20high%20detail%2C%20high%20contrast&width=700&height=700&seq=process-hero&orientation=portrait"
              alt="Diamond held by tweezers"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="border-t border-zayan-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-zayan-border reveal" ref={stepsRef}>
          {steps.map((s, i) => (
            <div key={i} className={`px-8 py-12 flex flex-col items-center text-center gap-4 relative hover:bg-zayan-gold/5 transition-colors duration-500`}>
              <span className="text-zayan-textDarker text-xs tracking-[0.2em] font-sans">{s.step}</span>
              <div className="w-16 h-16 flex items-center justify-center rounded-full border border-zayan-gold/50">
                <i className={`${s.icon} text-zayan-gold text-2xl`} />
              </div>
              {/* Arrow between steps */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
                  <i className="ri-arrow-right-line text-zayan-gold/40 text-lg" />
                </div>
              )}
              <h3 className="font-serif text-zayan-gold text-xl">{s.title}</h3>
              <div className="h-px w-8 bg-zayan-gold/40" />
              <p className="text-zayan-textMuted text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline Section */}
      <section className="border-t border-zayan-border">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Image */}
          <div className="relative overflow-hidden h-64 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=elegant%20diamond%20solitaire%20engagement%20ring%20with%20round%20brilliant%20diamond%20in%20gold%20setting%2C%20dark%20black%20background%2C%20luxury%20jewelry%20photography%2C%20side%20angle%20view%2C%20sparkling%20facets%2C%20professional%20studio%20lighting&width=700&height=500&seq=process-timeline&orientation=landscape"
              alt="Diamond ring"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-zayan-bg/60" />
          </div>

          {/* Right Content */}
          <div className="px-8 lg:px-16 py-16 flex flex-col justify-center reveal" ref={timelineRef}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">TIMELINE</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl text-zayan-gold mb-6">
              Estimated Delivery Timeline
            </h2>
            <p className="text-zayan-textMuted text-sm leading-relaxed mb-4 max-w-lg">
              Most sourced diamonds are delivered within approximately 2–3 weeks depending on sourcing availability, customs processing, and logistics timelines.
            </p>
            <p className="text-zayan-textMuted text-sm leading-relaxed mb-10 max-w-lg">
              <strong className="text-zayan-gold">Custom set jewelry</strong> — including rings, pendants, earrings, and other mounted pieces — requires a minimum of 25 additional days for design and crafting beyond the diamond delivery timeframe. Total production time for jewelry orders is therefore significantly longer and will be confirmed on a per-order basis.
            </p>
            <div className="grid grid-cols-2 gap-6">
              {guarantees.map((g, i) => (
                <div key={i} className={`flex items-start gap-3`}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
                    <i className={`${g.icon} text-zayan-gold text-sm`} />
                  </div>
                  <div>
                    <p className="text-zayan-gold text-xs tracking-[0.1em] font-sans mb-1">{g.title}</p>
                    <p className="text-zayan-textMuted text-xs leading-relaxed">{g.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}